/** «Куда планируем» «Подбора поставки»: галочки регионов-целей (по образцу PlanningTargets
 *  «Распределения»). Группы — «все регионы», регионы РФ, страны и «не определён»; у групповой
 *  галочки часть включённых — indeterminate. Логика и хранение — draftTargets.ts. */
import { Settings2 } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { groupState, isOn, setTargets, type RegionOpt, type Targets } from "./draftTargets";

function GroupCheck({
  label,
  regions,
  targets,
  onChange,
  className,
}: {
  label: string;
  regions: RegionOpt[];
  targets: Targets;
  onChange: (t: Targets) => void;
  className?: string;
}): React.ReactElement {
  const st = groupState(targets, regions);
  const offCount = regions.filter((r) => !isOn(targets, r)).length;
  return (
    <label className={cn("flex items-center gap-2 text-xs font-medium", className)}>
      <input
        type="checkbox"
        aria-label={label}
        ref={(el) => {
          if (el) el.indeterminate = st === "some";
        }}
        checked={st === "all"}
        disabled={regions.length === 0}
        // часть включена → включить все (как у «Распределения»)
        onChange={() => onChange(setTargets(targets, regions, st !== "all"))}
      />
      {label}
      {offCount > 0 && <span className="font-normal text-muted-foreground">· выключено {offCount}</span>}
    </label>
  );
}

function RegionList({
  regions,
  targets,
  onChange,
}: {
  regions: RegionOpt[];
  targets: Targets;
  onChange: (t: Targets) => void;
}): React.ReactElement {
  return (
    <div className="mt-1 grid gap-0.5 pl-6">
      {regions.map((r) => (
        <label key={r.id} className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={isOn(targets, r)}
            onChange={(e) => onChange(setTargets(targets, [r], e.target.checked))}
          />
          {r.label}
        </label>
      ))}
    </div>
  );
}

export const TargetsPopover = React.memo(function TargetsPopover({
  regions,
  targets,
  onChange,
  showOff,
  onShowOff,
}: {
  regions: RegionOpt[];
  targets: Targets;
  onChange: (t: Targets) => void;
  showOff: boolean;
  onShowOff: (v: boolean) => void;
}): React.ReactElement {
  const ru = regions.filter((r) => r.macro);
  const other = regions.filter((r) => !r.macro);
  const off = regions.filter((r) => !isOn(targets, r)).length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs"
          title="Регионы-цели поставки: выключенный регион не входит в суммы товара, «Заполнить до N дней» и выгрузку"
          data-testid="draft-targets"
        >
          <Settings2 className="size-4" />
          Куда планируем
          {off > 0 && (
            <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-700 dark:bg-amber-950 dark:text-amber-300">
              выкл {off}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-h-[70vh] w-80 overflow-auto p-3 text-sm" data-testid="draft-targets-popover">
        <p className="mb-2 text-xs text-muted-foreground">
          Выключенный регион — 0: не входит в суммы товара, «Заполнить до N дней» и выгрузку; введённое количество
          сохраняется и вернётся при включении. Настройка — в этом браузере.
        </p>
        <label className="mb-2 flex items-center gap-2 rounded border px-2 py-1 text-xs">
          <input type="checkbox" checked={showOff} onChange={(e) => onShowOff(e.target.checked)} />
          показывать выключенные в таблице
        </label>
        <GroupCheck label="все регионы" regions={regions} targets={targets} onChange={onChange} className="border-b pb-1" />
        <div className="mt-2">
          <GroupCheck label="Регионы РФ" regions={ru} targets={targets} onChange={onChange} />
          <RegionList regions={ru} targets={targets} onChange={onChange} />
        </div>
        {other.length > 0 && (
          <div className="mt-2">
            <GroupCheck label="Страны и «не определён»" regions={other} targets={targets} onChange={onChange} />
            <RegionList regions={other} targets={targets} onChange={onChange} />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
});
